/// <reference types="google.maps" />
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnInit, ViewChild} from '@angular/core';
import {ConfigService} from '@config/config.service';
import {AssociationsService} from '@shared/services/associations.service';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {map, take} from 'rxjs/operators';
import {BehaviorSubject, catchError, EMPTY, of} from 'rxjs';
import {OrganizationLocationData} from '@services/back-office/model/organizationLocationData';
import {ConfirmationDialogComponent} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {LocationPointModel} from '@services/back-office/model/locationPointModel';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import LatLngLiteral = google.maps.LatLngLiteral;
import PlaceResult = google.maps.places.PlaceResult;

@Component({
    selector: 'dah-map-polygon-dialog',
    templateUrl: './map-polygon-dialog.component.html',
    styleUrls: ['./map-polygon-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MapPolygonDialogComponent implements OnInit {
    @ViewChild('mapContainer') public mapContainerRef: ElementRef;
    @ViewChild('search') public searchElementRef: ElementRef;
    public autocomplete: google.maps.places.Autocomplete | undefined;
    public map: google.maps.Map;
    public drawingManagerEnabled: boolean;

    public originLocationData: OrganizationLocationData;
    public originEntityLocation: LocationPointModel[];
    public locationData: OrganizationLocationData;
    public loading$ = new BehaviorSubject(false);
    private polygon: google.maps.Polygon;
    private mapConfig: google.maps.MapOptions = this.config.googleMapsConfig;

    private drawingListeners: google.maps.MapsEventListener[] = [];
    private tempPolygon: google.maps.Polygon | null = null;
    private tempPath: LatLngLiteral[] = [];
    private previewLine: google.maps.Polyline | null = null;
    private tempPolyline: google.maps.Polyline | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            association?: AssociationListResponse,
            buildingId?: string,
            allowInexactly?: boolean,
            entityData?: {
                entityId: string,
                entityAddress?: string
                entityLocation?: LocationPointModel[],
            }
        },
        private dialogRef: MatDialogRef<MapPolygonDialogComponent>,
        private config: ConfigService,
        private associationsService: AssociationsService,
        private ngZone: NgZone,
        private sharedService: SharedService,
        private cdRef: ChangeDetectorRef,
        private toastr: ToastrService,
        private translateService: TranslateService
    ) {
    }

    ngOnInit() {
        if (this.data.entityData) {
            this.originEntityLocation = this.data.entityData.entityLocation || [];
            this.getEntityLocation();
            return;
        }

        if (this.data.association) {
            if (!this.data.association?.id) {
                this.closeDialog();
                return;
            }

            this.getLocation(this.data.buildingId ? this.data.buildingId : <string>this.data.association.id, !!this.data.buildingId);
            return;
        }
    }

    // const defaultCenter = { lat: 49.0, lng: 31.0 }; // Центр України
    async getEntityLocation() {
        const kyivCenter = {lat: 50.4501, lng: 30.5234}; // Київ
        const polyPoints = this.data.entityData?.entityLocation;
        const entityAddress = this.data.entityData?.entityAddress;

        const center = polyPoints?.length
            ? this.calculatePolygonCenter(polyPoints)
            : kyivCenter;

        const zoom = polyPoints?.length
            ? 8
            : 8;

        this.locationData = {
            center,
            polyPoints: polyPoints ?? []
        };

        this.mapConfig = {
            ...this.mapConfig,
            center,
            zoom
        };

        await this.buildMap();
        await this.initAutocomplete();

        this.drawMarker(center);
        if (polyPoints?.length) {
            this.drawPolygon();
        }

        // Якщо є адреса, але немає координат — вставляємо і тригеримо автокомпліт
        if (!polyPoints?.length && entityAddress) {
            setTimeout(() => {
                this.searchElementRef.nativeElement.value = entityAddress;
                google.maps.event.trigger(this.searchElementRef.nativeElement, 'focus');
            }, 1000);
        }
    }

    getLocation(id: string, byBuilding?: boolean) {
        if (!this.data.association) {
            return;
        }

        this.associationsService.getLocation(id, byBuilding).pipe(
            take(1),
            map((locationData: OrganizationLocationData) => {
                this.locationData = {...locationData, center: {...locationData.center || {lat: 50.4501, lng: 30.5234}}};
                this.originLocationData = {...locationData};
                this.cdRef.detectChanges();
                this.initAutocomplete().then();
                this.mapConfig = {
                    ...this.mapConfig,
                    ...locationData?.center?.lat && locationData?.center?.lng && {
                        center: {lat: locationData.center.lat, lng: locationData.center.lng},
                        zoom: this.data.association!.type == 'PRIVATE_SECTOR_COOP' || this.data.association!.type == 'GARAGE_COOP' ? 16 : 18
                    },
                };

                this.buildMap().then(() => {
                    if (locationData.center?.lat && locationData.center?.lng) {
                        this.drawMarker({lat: locationData.center.lat, lng: locationData.center.lng}).then();
                    }

                    if (locationData.polyPoints?.length) {
                        this.drawPolygon().then();
                    }
                });
            }),
            catchError(() => {
                this.closeDialog();
                return EMPTY;
            })
        ).subscribe();
    }

    async initAutocomplete() {
        const {Autocomplete} = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
        this.autocomplete = new Autocomplete(this.searchElementRef.nativeElement, {});

        this.autocomplete.addListener('place_changed', () => {
            this.ngZone.run(() => {
                const place: PlaceResult = this.autocomplete!.getPlace();
                const lat = place.geometry?.location?.lat();
                const lng = place.geometry?.location?.lng();
                if (lat && lng) {
                    this.mapConfig = {
                        ...this.mapConfig,
                        center: {lat, lng},
                        zoom: 17,
                    };

                    this.buildMap().then(() => {
                        this.drawMarker({lat, lng});
                        if (this.drawingManagerEnabled) {
                            // this.setDrawingManager();
                            if (this.drawingManagerEnabled) {
                                this.stopDrawingPolygon();
                                this.startDrawingPolygon();
                            }
                        }
                    });
                }
            });
        });
    }

    async buildMap() {
        const {Map} = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        this.map = new Map(this.mapContainerRef.nativeElement as HTMLElement, {...this.mapConfig, mapId: "4504f8b37365c3d0"});
    }

    async drawMarker(marker: LatLngLiteral) {
        if (!this.map || !marker?.lat || !marker?.lng) {
            return;
        }

        const {AdvancedMarkerElement} = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
        const map = this.map;
        const draggableMarker = new AdvancedMarkerElement({
            map,
            position: marker,
            gmpDraggable: true
        });

        draggableMarker.addListener('dragend', () => {
            const center = JSON.parse(JSON.stringify(draggableMarker.position as google.maps.LatLng));
            this.updateLocationData({center});
        });
    }

    toggleDrawingMode() {
        if (this.drawingManagerEnabled) {
            this.stopDrawingPolygon();
        } else {
            this.startDrawingPolygon();
        }

        this.cdRef.detectChanges();
    }

    private async startDrawingPolygon() {
        if (!this.map) {
            return;
        }

        this.drawingManagerEnabled = true;
        this.tempPath = [];

        this.map.setOptions({
            draggable: false,
            disableDoubleClickZoom: true,
            draggableCursor: 'crosshair'
        });

        this.polygon?.setMap(null);
        this.tempPolygon?.setMap(null);
        this.previewLine?.setMap(null);

        const {Polyline} = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

        this.tempPolyline = new Polyline({
            map: this.map,
            path: this.tempPath,
            strokeColor: "#339437",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            clickable: false
        });

        this.previewLine = new Polyline({
            map: this.map,
            path: [],
            strokeColor: "#339437",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            clickable: false
        });

        const clickListener = this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (!event.latLng) {
                return;
            }

            const clickedPoint: LatLngLiteral = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
            };

            if (this.tempPath.length >= 3 && this.isCloseToFirstPoint(clickedPoint)) {
                this.finishDrawingPolygon();
                return;
            }

            this.tempPath.push(clickedPoint);
            this.tempPolyline!.setPath(this.tempPath);
        });

        const mouseMoveListener = this.map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
            if (!event.latLng || !this.previewLine || !this.tempPath.length) {
                return;
            }

            const lastPoint = this.tempPath[this.tempPath.length - 1];

            this.previewLine.setPath([
                lastPoint,
                {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                }
            ]);
        });

        const dblClickListener = this.map.addListener('dblclick', (event: google.maps.MapMouseEvent) => {
            event.stop?.();
            this.finishDrawingPolygon();
        });

        this.drawingListeners.push(clickListener, mouseMoveListener, dblClickListener);

        this.cdRef.detectChanges();
    }

    private isCloseToFirstPoint(point: LatLngLiteral): boolean {
        if (!this.tempPath.length) {
            return false;
        }

        const firstPoint = this.tempPath[0];

        const distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(point.lat, point.lng),
            new google.maps.LatLng(firstPoint.lat, firstPoint.lng)
        );

        return distance < 20;
    }

    private finishDrawingPolygon() {
        if (this.tempPath.length < 3) {
            this.toastr.warning('Для полігону потрібно мінімум 3 точки');
            return;
        }

        const polyPoints = [...this.tempPath];
        const center = this.calculatePolygonCenter(polyPoints);

        this.updateLocationData({polyPoints, center});

        this.tempPolygon?.setMap(null);
        this.tempPolygon = null;

        this.tempPolyline?.setMap(null);
        this.tempPolyline = null;

        this.previewLine?.setMap(null);
        this.previewLine = null;

        this.stopDrawingPolygon(false);
        this.drawPolygon();

        this.cdRef.detectChanges();
    }

    private stopDrawingPolygon(clearTemp = true) {
        this.drawingManagerEnabled = false;

        this.drawingListeners.forEach(listener => listener.remove());
        this.drawingListeners = [];

        this.map?.setOptions({
            draggable: true,
            disableDoubleClickZoom: false,
            draggableCursor: undefined
        });

        if (clearTemp) {
            this.tempPath = [];
            this.tempPolygon?.setMap(null);
            this.tempPolygon = null;
        }

        this.previewLine?.setMap(null);
        this.previewLine = null;

        this.cdRef.detectChanges();
    }

    async drawPolygon(editable?: boolean) {
        if (!this.locationData?.polyPoints?.length) {
            return;
        }

        this.polygon?.setMap(null);

        const {Polygon} = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        this.polygon = new Polygon({
            paths: this.locationData.polyPoints,
            strokeColor: "#339437",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#63BE67",
            fillOpacity: 0.35,
            editable: !!editable,
            draggable: !!editable
        });

        this.polygon.setMap(this.map);

        if (editable) {
            google.maps.event.addListener(
                this.polygon,
                'mouseup',
                () => {
                    const polyPoints = JSON.parse(JSON.stringify(this.polygon.getPath().getArray()));
                    const center = this.calculatePolygonCenter(polyPoints);
                    this.updateLocationData({ polyPoints, center });
                }
            );
        }
    }

    clearPolygon() {
        this.locationData = {...this.locationData?.center && {center: this.locationData.center}};
        this.polygon.setMap(null);
    }

    updateLocationData(data: OrganizationLocationData) {
        this.locationData = {
            ...this.locationData,
            ...data.polyPoints && {polyPoints: data.polyPoints},
            ...data.center && {center: data.center}
        };

        this.cdRef.detectChanges();
    }

    closeDialog() {
        if (this.data.entityData) {
            if (JSON.stringify(this.locationData.polyPoints) == JSON.stringify(this.originEntityLocation)) {
                this.dialogRef.close();
                return;
            }

            const dialog$ = this.sharedService.openDialog(
                ConfirmationDialogComponent,
                {...this.config.confirmationDialog, hasBackdrop: true},
                {
                    title: 'Зберегти зміни?',
                    cancelText: 'common.buttons.no',
                    submitText: 'common.buttons.yes',
                }
            );

            dialog$.afterClosed().pipe(
                take(1),
            ).subscribe((result: any) => {
                if (result) {
                    this.dialogRef.close({
                        result: true, data: {
                            katottgId: this.data.entityData!.entityId,
                            locationData: this.locationData
                        }
                    });
                } else {
                    this.dialogRef.close();
                }
            });

        }

        if (this.data.association) {
            if (JSON.stringify(this.locationData) == JSON.stringify(this.originLocationData)) {
                this.dialogRef.close();
                return;
            }

            const dialog$ = this.sharedService.openDialog(
                ConfirmationDialogComponent,
                {...this.config.confirmationDialog, hasBackdrop: true},
                {
                    title: 'Зберегти зміни?',
                    cancelText: 'common.buttons.no',
                    submitText: 'common.buttons.yes',
                }
            );

            dialog$.afterClosed().pipe(
                take(1),
            ).subscribe((result: any) => {
                if (result) {
                    if (this.locationData?.polyPoints?.length && this.locationData?.polyPoints?.length < 3) {
                        this.toastr.warning('Щось не схоже, що тут є що зберігати');
                        return;
                    }

                    this.loading$.next(true);

                    this.associationsService.setLocation(
                        {...this.locationData, organizationId: this.data.association!.id!, ...this.data.buildingId && {buildingId: this.data.buildingId}}
                    ).pipe(
                        take(1),
                        catchError((error: HttpErrorResponse) => {
                            SharedService.showErrorWithTitle(
                                this.translateService,
                                this.toastr,
                                `responseErrors.${error.message}`
                            );
                            this.loading$.next(false);
                            return of();
                        })
                    ).subscribe(() => {
                        this.loading$.next(true);
                        this.dialogRef.close({result: true, data: this.locationData});
                    });
                } else {
                    this.dialogRef.close();
                }
            });
        }
    }

    private calculatePolygonCenter(points: LocationPointModel[]): LatLngLiteral {
        const validPoints = points.filter(p => p.lat !== undefined && p.lng !== undefined);
        const latSum = validPoints.reduce((sum, p) => sum + (p.lat ?? 0), 0);
        const lngSum = validPoints.reduce((sum, p) => sum + (p.lng ?? 0), 0);
        const count = validPoints.length || 1;

        return {
            lat: latSum / count,
            lng: lngSum / count
        };
    }
}
